import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useParams } from 'react-router-dom';
import PageHeaders from '../../../../../Utils/PageHeaders';
import moment from 'moment';
import { fetchBlog, fetchBlogs, fetchWordpressPostByLink } from '../../api';
import Error404 from '../../../../404/Error404';

const useStyles = makeStyles((theme) => ({
  metadata: {
    color: '#64748B',
    fontSize: '14px',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 24,
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
    fontWeight: 600,
    color: '#1E293B',
    marginBottom: theme.spacing(4),
    lineHeight: 1.3,
  },
  content: {
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      marginBottom: theme.spacing(2),
    },
    '& p': {
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

const BlogContent = () => {
  const classes = useStyles();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();

  useEffect(() => {
    const load = async () => {
      try {
        let blog = await fetchBlog(slug);
        if (!blog) {
          const list = await fetchBlogs();
          blog = list.find((b) => String(b.slug) === String(slug) || String(b.id) === String(slug)) || null;
        }
        if (blog && !blog.content && blog.link) {
          const wp = await fetchWordpressPostByLink(blog.link);
          if (wp?.content) {
            blog = { ...blog, content: wp.content };
          }
        }
        setPost(blog);
        setLoading(false);
      } catch (err) {
        setError('Failed to load blog');
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  if (!post) {
    return <Error404 />;
  }

  return (
    <Box>
      <PageHeaders
        title={`${post?.heading || post?.title || 'Blog Post'} - Plootus Blogs`}
        description={post?.description || post?.excerpt || 'Read this insightful blog post on Plootus about financial planning and retirement.'}
        keywords={`Plootus blog, ${post?.heading || post?.title || ''}, financial advice, retirement planning, investment tips`}
        ogType="article"
        ogUrl={`https://www.plootus.com/blogs/${slug}`}
        ogImage={post?.imageUrl || post?.image}
        canonicalUrl={`https://www.plootus.com/blogs/${slug}`}
        additionalMeta={{
          'article:published_time': post?.date,
        }}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post?.heading || post?.title || '',
          description: post?.description || post?.excerpt || '',
          image: post?.imageUrl || post?.image || '',
          datePublished: post?.date,
          author: {
            '@type': 'Organization',
            name: 'Plootus',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Plootus',
            logo: {
              '@type': 'ImageObject',
              url: 'https://www.plootus.com/logo.png',
            },
          },
        }}
      />
      <Typography className={classes.metadata}>
        {post?.date ? `Posted on ${moment(post.date).format('MMMM D, YYYY')}` : ''}
      </Typography>

      <Typography variant="h1" className={classes.title}>
        {post?.heading || post?.title || ''}
      </Typography>

      {(post?.imageUrl || post?.image) && (
        <Box
          component="img"
          src={post.imageUrl || post.image}
          alt={post.heading || post.title}
          sx={{ width: '100%', height: { xs: 200, md: 400 }, objectFit: 'cover', borderRadius: 2, mb: 3 }}
        />
      )}

      <Box
        className={classes.content}
        dangerouslySetInnerHTML={{
          __html: (() => {
            const raw = post?.content || post?.contentHtml || post?.html || post?.htmlData || '';
            if (typeof raw === 'string' && /<[^>]+>/.test(raw)) return raw;
            if (raw) return String(raw).replace(/\n/g, '<br />');
            return post?.excerpt || post?.description || '';
          })(),
        }}
      />
    </Box>
  );
};

export default BlogContent;
